import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Award, ShieldCheck, MapPin, CheckCircle2, ArrowRight } from "lucide-react";

function useSEO({ title, description }) {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = title;
        const meta = document.querySelector('meta[name="description"]');
        const prev = meta?.getAttribute("content");
        meta?.setAttribute("content", description);
        return () => {
            document.title = prevTitle;
            if (prev) meta?.setAttribute("content", prev);
        };
    }, [title, description]);
}

export default function HeroesDiscount() {
    useSEO({
        title:
            "Military, Veteran & First Responder Mobility Scooter Discount in Orlando",
        description:
            "$20 off mobility scooter rentals in Orlando, Kissimmee, Lake Buena Vista, Walt Disney World, Universal Orlando, International Drive, and Port Canaveral for active-duty military, veterans, police, firefighters, EMTs, and paramedics. Verify online and reserve a Pride Victory 10 with free delivery across Central Florida.",
    });

    return (
        <div data-testid="heroes-discount-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-[color:var(--brand-navy)] text-white">
                <div className="mx-auto max-w-7xl px-4 pb-16 pt-20 sm:px-6 lg:px-8 lg:pt-28">
                    <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-gold)]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-gold)]">
                        <Award className="h-3.5 w-3.5" />
                        $20 off · Heroes Discount
                    </div>
                    <h1 className="mt-6 max-w-3xl font-display text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                        Military, Veteran &amp; First Responder Mobility
                        Scooter Discount in{" "}
                        <span className="text-[color:var(--brand-gold)]">
                            Orlando
                        </span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base font-medium leading-relaxed text-white/95 sm:text-lg">
                        A small thank-you for those who serve. Verify your
                        eligibility and receive a private $20 discount code on
                        any Pride Victory 10 mobility scooter rental across
                        Central Florida — Walt Disney World, Universal Orlando,
                        Lake Buena Vista, International Drive, Kissimmee, and
                        Port Canaveral.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <Link
                            to="/scooters/pride-victory-10"
                            data-testid="heroes-page-book"
                            className="hero-cta pill-btn inline-flex items-center gap-2 text-white"
                        >
                            Reserve a Scooter
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                        <a
                            href="#verify"
                            data-testid="heroes-page-verify"
                            className="pill-btn inline-flex items-center gap-2 border border-white/40 bg-white/10 text-white backdrop-blur hover:bg-white/20"
                        >
                            How to verify
                        </a>
                    </div>
                </div>
            </section>

            {/* ELIGIBILITY */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                        Who Qualifies
                    </div>
                    <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-4xl">
                        Eligible groups
                    </h2>
                    <p className="mt-4 text-slate-600">
                        The $20 discount is available to those currently or
                        formerly serving our community.
                    </p>
                </div>
                <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {[
                        "Veterans (all branches)",
                        "Active-duty military",
                        "Police officers",
                        "Firefighters",
                        "EMTs",
                        "Paramedics",
                    ].map((g) => (
                        <div
                            key={g}
                            className="card-surface flex items-center gap-3 p-5"
                            data-testid={`heroes-elig-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                            <CheckCircle2 className="h-5 w-5 text-[color:var(--brand-green)]" />
                            <span className="font-semibold text-[color:var(--brand-navy)]">
                                {g}
                            </span>
                        </div>
                    ))}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="verify" className="bg-white">
                <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                    <div className="max-w-2xl">
                        <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                            How It Works
                        </div>
                        <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-4xl">
                            Three simple steps
                        </h2>
                    </div>
                    <ol className="mt-10 grid gap-6 md:grid-cols-3">
                        {[
                            {
                                n: "1",
                                title: "Submit verification",
                                body: "Tap the Heroes Discount bubble in the corner of any page, upload proof of eligibility, and provide your name, email, and phone.",
                            },
                            {
                                n: "2",
                                title: "We review promptly",
                                body: "We manually verify each request, usually within one business day. Your document is deleted after verification.",
                            },
                            {
                                n: "3",
                                title: "Get your private $20 code",
                                body: "You'll receive a single-use $20 code by email — apply it at checkout on the Pride Victory 10 booking page.",
                            },
                        ].map((s) => (
                            <li key={s.n} className="card-surface p-8">
                                <div className="grid h-11 w-11 place-items-center rounded-full bg-[color:var(--brand-navy)] font-display text-xl font-extrabold text-white">
                                    {s.n}
                                </div>
                                <h3 className="mt-4 font-display text-xl font-extrabold text-[color:var(--brand-navy)]">
                                    {s.title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                    {s.body}
                                </p>
                            </li>
                        ))}
                    </ol>
                    <div className="mt-8 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                        <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
                        <p>
                            <strong>Privacy notice:</strong> Please cover Social
                            Security numbers, addresses, identification
                            numbers, and other sensitive information before
                            uploading. We only need to see your name and proof
                            of eligibility. Uploaded documents are stored
                            securely and deleted after verification.
                        </p>
                    </div>
                </div>
            </section>

            {/* SERVICE AREA */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                        Service Area
                    </div>
                    <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-4xl">
                        Delivery across Central Florida
                    </h2>
                    <p className="mt-4 text-slate-600">
                        Free delivery to Orlando and Kissimmee — including
                        Walt Disney World resorts, Universal Orlando hotels,
                        SeaWorld area, International Drive, and Lake Buena
                        Vista. Cruise passengers can add our $99 Port
                        Canaveral Concierge service on top of the Heroes
                        Discount.
                    </p>
                </div>
                <div className="mt-10 grid gap-6 md:grid-cols-3">
                    {[
                        "Walt Disney World Resorts",
                        "Universal Orlando Resort Hotels",
                        "SeaWorld Area",
                        "International Drive",
                        "Lake Buena Vista",
                        "Kissimmee & 192 corridor",
                        "Downtown Orlando",
                        "Orange County Convention Center",
                        "Port Canaveral Cruise Terminal",
                    ].map((zone) => (
                        <div
                            key={zone}
                            className="card-surface flex items-center gap-3 p-5"
                        >
                            <MapPin className="h-5 w-5 text-[color:var(--brand-gold)]" />
                            <span className="text-sm font-semibold text-[color:var(--brand-navy)]">
                                {zone}
                            </span>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div className="relative overflow-hidden rounded-3xl bg-[color:var(--brand-navy)] p-10 sm:p-14">
                    <div className="grain absolute inset-0" />
                    <div className="relative flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:justify-between">
                        <div className="max-w-2xl">
                            <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-gold)]">
                                Ready to reserve?
                            </div>
                            <h3 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                                Book your Pride Victory 10 — save $20 with
                                verification.
                            </h3>
                            <p className="mt-3 text-slate-300">
                                Verify below, then apply your private code at
                                checkout.
                            </p>
                        </div>
                        <Link
                            to="/scooters/pride-victory-10"
                            data-testid="heroes-cta-book"
                            className="hero-cta pill-btn inline-flex items-center gap-2 text-white"
                        >
                            Book Now
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
